import React, { useState, useEffect } from 'react';
import { Youtube, Calendar, User, Play, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

export default function SermonArchive() {
  const [latestSermons, setLatestSermons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLatestSermons();
  }, []);

  async function fetchLatestSermons() {
    try {
      const { data, error } = await supabase
        .from('sermon_materials')
        .select('*')
        .order('recorded_date', { ascending: false })
        .limit(3);

      if (error) throw error;
      setLatestSermons(data || []);
    } catch (error) {
      console.error('Error fetching sermons:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div id="sermon-archive" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Latest Sermons</h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Watch our most recent sermons and be encouraged in your faith through the teaching of God's Word.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {isLoading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="relative aspect-video bg-gray-100 animate-pulse">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            latestSermons.map((sermon) => (
              <a
                key={sermon.id}
                href={`https://www.youtube.com/watch?v=${sermon.youtube_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-video bg-gray-900">
                  <img
                    src={`https://img.youtube.com/vi/${sermon.youtube_id}/maxresdefault.jpg`}
                    alt={sermon.title}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-primary-600/90 rounded-full flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                      <Play className="text-white ml-1" size={32} />
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {sermon.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <User className="mr-1" size={14} />
                      <span>{sermon.preacher}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="mr-1" size={14} />
                      <span>{format(new Date(sermon.recorded_date), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
              </a>
            ))
          )}
        </div>

        <div className="text-center">
          <Link
            to="/sermons"
            className="inline-flex items-center bg-primary-600 text-white px-8 py-4 rounded-full hover:bg-primary-700 transition-colors"
          >
            View All Sermons
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </div>
    </div>
  );
}